import { useCallback } from 'react'
import { useTranslation } from 'react-i18next'

const fieldClass =
	'w-full rounded-md border border-gray-300 bg-white px-2 py-1.5 text-sm text-gray-900 focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500'
const readOnlyClass =
	'w-full rounded-md border border-transparent bg-gray-50 px-2 py-1.5 text-sm text-gray-700'

const toNum = v => (v === '' || v == null ? 0 : Number(v)) || 0

const calcLine = (price, count, taxPercent) => {
	const p = toNum(price)
	const c = toNum(count)
	const tax = toNum(taxPercent)
	const subtotal = +(p * c).toFixed(2)
	const taxValue = +((subtotal * tax) / 100).toFixed(2)
	const total = +(subtotal + taxValue).toFixed(2)
	return { price: p, count: c, taxPercent: tax, subtotal, taxValue, total }
}

export default function CorrectionProductTable({
	products = [],
	setAfterField,
	fmtMoney,
	readOnly = false,
}) {
	const { t } = useTranslation()

	const stopWheel = useCallback(e => e.currentTarget.blur(), [])

	const handleAfterChange = useCallback(
		e => {
			const idx = Number(e.currentTarget.dataset.index)
			const field = e.currentTarget.dataset.field
			setAfterField(idx, field, e.currentTarget.value)
		},
		[setAfterField]
	)

	return (
		<div className="overflow-x-auto rounded-lg border border-gray-200">
			<table className="min-w-full divide-y divide-gray-200 text-sm">
				<thead className="bg-gray-50">
					<tr>
						<th className="px-3 py-2 text-left font-semibold text-gray-700">{t('korekty.form.products.name')}</th>
						<th className="px-3 py-2 text-left font-semibold text-gray-700" colSpan={3}>
							{t('korekty.form.products.beforeTitle')}
						</th>
						<th className="px-3 py-2 text-left font-semibold text-gray-700" colSpan={3}>
							{t('korekty.form.products.afterTitle')}
						</th>
						<th className="px-3 py-2 text-left font-semibold text-gray-700" colSpan={2}>
							{t('korekty.form.products.correctionTitle')}
						</th>
					</tr>
					<tr className="bg-gray-50 text-xs text-gray-500">
						<th className="px-3 py-1" />
						<th className="px-3 py-1 text-left">{t('korekty.form.products.count')}</th>
						<th className="px-3 py-1 text-left">{t('korekty.form.products.price')}</th>
						<th className="px-3 py-1 text-left">{t('korekty.form.products.vatPercent')}</th>
						<th className="px-3 py-1 text-left">{t('korekty.form.products.count')}</th>
						<th className="px-3 py-1 text-left">{t('korekty.form.products.price')}</th>
						<th className="px-3 py-1 text-left">{t('korekty.form.products.vatPercent')}</th>
						<th className="px-3 py-1 text-left">{t('korekty.form.products.netDiff')}</th>
						<th className="px-3 py-1 text-left">{t('korekty.form.products.totalDiff')}</th>
					</tr>
				</thead>
				<tbody className="divide-y divide-gray-100 bg-white">
					{products.map((row, index) => {
						const before = calcLine(row.before?.price, row.before?.count, row.before?.taxPercent)
						const after = calcLine(row.after?.price, row.after?.count, row.after?.taxPercent)
						const diffSubtotal = +(after.subtotal - before.subtotal).toFixed(2)
						const diffTotal = +(after.total - before.total).toFixed(2)

						return (
							<tr key={index}>
								<td className="px-3 py-2 align-top">
									<div className="font-medium text-gray-900">{row.name || '—'}</div>
								</td>

								{/* Przed korektą */}
								<td className="px-3 py-2 align-top">
									<input className={readOnlyClass} value={before.count} readOnly tabIndex={-1} />
								</td>
								<td className="px-3 py-2 align-top">
									<input className={readOnlyClass} value={before.price} readOnly tabIndex={-1} />
								</td>
								<td className="px-3 py-2 align-top">
									<input className={readOnlyClass} value={before.taxPercent} readOnly tabIndex={-1} />
								</td>

								{/* Po korekcie */}
								<td className="px-3 py-2 align-top">
									<input
										type="number"
										step="any"
										className={readOnly ? readOnlyClass : fieldClass}
										value={row.after?.count ?? ''}
										data-index={index}
										data-field="count"
										onChange={handleAfterChange}
										onWheel={stopWheel}
										readOnly={readOnly}
									/>
								</td>
								<td className="px-3 py-2 align-top">
									<input
										type="number"
										step="any"
										className={readOnly ? readOnlyClass : fieldClass}
										value={row.after?.price ?? ''}
										data-index={index}
										data-field="price"
										onChange={handleAfterChange}
										onWheel={stopWheel}
										readOnly={readOnly}
									/>
								</td>
								<td className="px-3 py-2 align-top">
									<input
										type="number"
										step="any"
										className={readOnly ? readOnlyClass : fieldClass}
										value={row.after?.taxPercent ?? ''}
										data-index={index}
										data-field="taxPercent"
										onChange={handleAfterChange}
										onWheel={stopWheel}
										readOnly={readOnly}
									/>
								</td>

								{/* Korekta */}
								<td className="px-3 py-2 align-top">
									<span className={`font-medium ${diffSubtotal < 0 ? 'text-red-600' : 'text-emerald-700'}`}>
										{fmtMoney(diffSubtotal)}
									</span>
								</td>
								<td className="px-3 py-2 align-top">
									<span className={`font-semibold ${diffTotal < 0 ? 'text-red-600' : 'text-emerald-700'}`}>
										{fmtMoney(diffTotal)}
									</span>
								</td>
							</tr>
						)
					})}
				</tbody>
			</table>
		</div>
	)
}

export { calcLine }
